'use client';

import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { drawImageSafe } from 'utils/drawImageSafe';
import { Container } from '..';
import { H2Large } from './styleConstants';
import { useTranslations } from 'next-intl';
import styles from './ToggleAnimationSection.module.css';
// import { SPEEDSIZE_CDN } from 'lib/config/assetUrls';

// const SPEEDSIZE_CLIENT_ID = process.env.NEXT_PUBLIC_SPEEDSIZE_CLIENT_ID;

export const ToggleAnimationSection = ({
  className,
  onLoad,
}: {
  className?: string;
  onLoad?: () => void;
}) => {
  const t = useTranslations('powerplugs');
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const [screenSize, setScreenSize] = useState<
    'sm' | 'md' | 'lg' | 'xl' | null
  >(null);
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const gsapContext = useGsapContext();

  const resolutionMap = {
    sm: {
      tag: 'w_2340',
      width: 1080,
      height: 2346,
      dir: 'sm-v3',
      start: 0,
      frameStep: 1,
      frameCount: 72,
    },
    md: {
      tag: 'w_2340',
      width: 1080,
      height: 2346,
      dir: 'sm-v3',
      start: 0,
      frameStep: 1,
      frameCount: 72,
    },
    lg: {
      tag: 'w_1920',
      width: 1920,
      height: 1080,
      dir: 'lg-v3',
      start: 0,
      frameStep: 1,
      frameCount: 137,
    },
    xl: {
      tag: 'w_3840',
      width: 3840,
      height: 2160,
      dir: 'lg-v3',
      start: 0,
      frameStep: 1,
      frameCount: 137,
    },
  };

  const animationInit = () => {
    if (!window || !container.current || !canvas.current || !screenSize) return;

    const frameCount = resolutionMap[screenSize].frameCount;

    const canvasEl = canvas.current;
    const context = canvasEl.getContext('2d');

    canvasEl.width = resolutionMap[screenSize].width;
    canvasEl.height = resolutionMap[screenSize].height;

    const currentFrame = (index: number) =>
      getCompressedAssetUrl(
        `/web_v2/sequence/powerplugs-toggle/${resolutionMap[screenSize].dir}/${(
          index + 1
        )
          .toString()
          .padStart(4, '0')}.png/${resolutionMap[screenSize].tag}`,
      );

    if (context) {
      const img = new Image();
      img.src = currentFrame(0);
      img.onload = () => {
        drawImageSafe(context, img, 0, 0);
      };
    }

    const images: any = [];

    const ring = {
      frame: 0,
    };

    let imageLoadIndex = 0;
    const onImageLoad = () => {
      imageLoadIndex++;

      if (imageLoadIndex >= images.length - 1 && onLoad) onLoad();
    };

    for (let i = 0; i < frameCount; i += resolutionMap[screenSize].frameStep) {
      const img = new Image();
      img.onload = onImageLoad;
      img.src = currentFrame(i);
      images.push(img);
    }

    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: { duration: 1 },
        scrollTrigger: {
          immediateRender: false,
          trigger: container.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.15,
        },
      });

      (window as any)['tltl'] = tl;

      const render = () => {
        const img = images[ring.frame];
        if (!img?.complete || img.naturalWidth === 0) return;
        if (!context) return;
        requestAnimationFrame(() => {
          context.clearRect(0, 0, canvasEl.width, canvasEl.height);
          drawImageSafe(context, img, 0, 0);
        });
      };

      tl.fromTo(
        ring,
        {
          frame: resolutionMap[screenSize].start,
        },
        {
          frame: images.length - 1,
          snap: 'frame',
          ease: 'ease-in',
          onUpdate: render, // use animation onUpdate instead of scrollTrigger's onUpdate
        },
      );

      tl.pause();
    });
  };

  useEffect(() => {
    if (window.innerWidth < 768) setScreenSize('sm');
    else if (window.innerWidth < 1025) setScreenSize('md');
    else if (window.innerWidth < 2000) setScreenSize('lg');
    else setScreenSize('xl');
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!screenSize || !container.current) return;

    intersectionObserver.current = new IntersectionObserver(
      (e) => {
        if (e[0].intersectionRatio <= 0) return;
        animationInit();
      },
      {
        root: null,
        rootMargin: '800px',
        threshold: 0.01,
      },
    );

    intersectionObserver.current?.observe(container.current);

    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, [screenSize]);

  return (
    <section
      className={[styles.root, className].filter(Boolean).join(' ')}
      ref={container}
    >
      <div className={styles.sticky}>
        {/* <div className="sticky-content"> */}
        <canvas ref={canvas}></canvas>
        <Container style={{ position: 'relative' }}>
          <div className={styles.textContainer}>
            <H2Large>
              {t('powerplugsPage.sectionTitles.pickYourLine1')}
              <br />
              {t('powerplugsPage.sectionTitles.pickYourLine2')}
            </H2Large>
          </div>
        </Container>
        {/* </div> */}
      </div>
    </section>
  );
};
